import { z } from "zod"

import { documentSchema } from "./documents.ts"
import { expenseSchema } from "./expenses.ts"
import { extractionSchema } from "./extractions.ts"
import {
  customerSyncSchema,
  invoiceSyncSchema,
  organisationBrandingSyncSchema,
  paymentReceiptSyncSchema,
  paymentSyncSchema,
  sellerProfileSyncSchema,
} from "./invoice-sync.ts"
import { logbookPeriodSchema } from "./logbook-periods.ts"
import { odometerReadingSchema } from "./odometer-readings.ts"
import { revisionSchema, syncEntitySchema } from "./sync.ts"
import { tripTrackSchema } from "./trip-tracks.ts"
import { tripSchema } from "./trips.ts"
import { vehicleSchema } from "./vehicles.ts"

/**
 * GET /api/v1/changes (ADR-015, T131): the pull half of sync. Every change carries the
 * entity document inline, so a client never needs one request per record. Wire rules for
 * client authors: docs/specs/sync-pull-api.md.
 */

/** Page size when `limit` is not sent. */
export const CHANGES_DEFAULT_LIMIT = 100
/** Largest page; more is a 422 VALIDATION_FAILED. */
export const CHANGES_MAX_LIMIT = 200

const queryInteger = z.string().regex(/^\d{1,15}$/, "Must be a non-negative integer")

/**
 * The query string of GET /api/v1/changes, as the strings `URLSearchParams` yields. Strict:
 * an unknown parameter or a malformed value is a VALIDATION_FAILED, never silently ignored.
 */
export const changesQuerySchema = z.strictObject({
  /** Cursor: the `cursor` of the previous page; 0 (the default) starts at the beginning. */
  since: queryInteger.transform(Number).default(0),
  limit: queryInteger
    .transform(Number)
    .pipe(z.number().int().min(1).max(CHANGES_MAX_LIMIT))
    .default(CHANGES_DEFAULT_LIMIT),
  /**
   * `generation` of the previous response (ADR-017 2.4). A mismatch is a 410 CURSOR_EXPIRED with
   * `details.reason = "access_changed"`. Only clients that have seen a `generation` send it.
   */
  generation: queryInteger.transform(Number).optional(),
  /**
   * Comma separated subset of the sync entities; absent means DEFAULT_CHANGES_ENTITIES (the
   * original four), so the receipts entities must be asked for explicitly and /v1 stays additive.
   */
  entities: z
    .string()
    .transform((value) => value.split(","))
    .pipe(z.array(syncEntitySchema).min(1))
    .transform((entities) => [...new Set(entities)])
    .optional(),
})

export type ChangesQuery = z.infer<typeof changesQuerySchema>

const changeItemBase = z.object({
  seq: z.number().int().nonnegative(),
  entity_id: z.uuid(),
  /** Revision the change log recorded; `document.revision` can be newer (apply by revision). */
  revision: revisionSchema,
  /** True for a tombstone: the record is soft-deleted and `document` is null. */
  deleted: z.boolean(),
  device_id: z.string().nullable().optional().default(null),
  local_seq: z.number().int().nullable().optional().default(null),
  created_at: z.string().datetime({ offset: true }),
})

/**
 * Single change record (s4-trips-api-brief section 2.5, ADR-015). `document` is the current
 * server version of the record (lenient stored schema of the entity); it is null for a
 * tombstone, and absent in payloads from before T131. For `trip_tracks` the `entity_id` is
 * the trip id.
 */
export const syncChangeItemSchema = z.discriminatedUnion("entity", [
  changeItemBase.extend({
    entity: z.literal("trips"),
    document: tripSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("vehicles"),
    document: vehicleSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("odometer_readings"),
    document: odometerReadingSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("trip_tracks"),
    document: tripTrackSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("documents"),
    document: documentSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("extractions"),
    document: extractionSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("expenses"),
    document: expenseSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("logbook_periods"),
    document: logbookPeriodSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("customers"),
    document: customerSyncSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("invoices"),
    document: invoiceSyncSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("payments"),
    document: paymentSyncSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("payment_receipts"),
    document: paymentReceiptSyncSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("organisation_branding"),
    document: organisationBrandingSyncSchema.nullable().optional().default(null),
  }),
  changeItemBase.extend({
    entity: z.literal("seller_profiles"),
    document: sellerProfileSyncSchema.nullable().optional().default(null),
  }),
])

export type SyncChangeItem = z.infer<typeof syncChangeItemSchema>

/**
 * Response payload for GET /api/v1/changes. `cursor` is the seq of the last change returned
 * (the request's `since` when the page is empty). `floor_seq` is the highest seq that the
 * retention job has compacted (0 until it has run).
 */
export const changesResponseSchema = z.object({
  cursor: z.number().int().nonnegative(),
  has_more: z.boolean(),
  floor_seq: z.number().int().nonnegative().optional().default(0),
  /**
   * The caller's access generation in this organisation (ADR-017 2.4). Absent on servers that
   * predate Teams; store it with the cursor and send it back as `generation`.
   */
  generation: z.number().int().nonnegative().optional(),
  changes: z.array(syncChangeItemSchema),
})

export type ChangesResponse = z.infer<typeof changesResponseSchema>
